/**
 * Personalized Nameplate Component
 *
 * 处理个性化铭牌的交互功能:
 * - 颜色选择并更新预览背景
 * - 输入框字符限制(最多10个字符)
 * - 打字机效果展示用户输入
 * - 根据字符数动态调整字号
 */
class PersonalizedNameplate extends HTMLElement {
  constructor() {
    super();
    
    this.input = this.querySelector('[data-nameplate-input]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.textDisplay = this.querySelector('[data-text-display]');
    this.previewBg = this.querySelector('.personalized-nameplate__preview-bg');
    this.colorSelector = this.querySelector('[data-color-selector]');
    this.colorInputs = this.querySelectorAll('input[name="nameplate-color"]');
    
    this.currentText = '';
    this.isTyping = false;
    
    this.init();
  }

  init() {
    if (!this.input || !this.submitButton || !this.textDisplay) return;
    
    // 确保文本显示区域始终可见
    this.textDisplay.classList.add('is-visible');
    
    // 监听颜色选择变化
    this.colorInputs.forEach(input => {
      input.addEventListener('change', this.handleColorChange.bind(this));
    });
    
    // 监听输入框变化(用于字符限制)
    this.input.addEventListener('input', this.handleInputChange.bind(this));
    
    // 监听按钮点击
    this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
    
    // 监听回车键提交
    this.input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.handleSubmit();
      }
    });
    
    // 初始化颜色(如果有默认选中的)
    const checkedColor = this.querySelector('input[name="nameplate-color"]:checked');
    if (checkedColor) {
      const colorCircle = checkedColor.nextElementSibling;
      this.updatePreviewColor(colorCircle);
    }
  }

  /**
   * 处理颜色选择变化
   */
  handleColorChange(event) {
    const colorCircle = event.target.nextElementSibling; // 获取对应的颜色圆圈
    this.updatePreviewColor(colorCircle);
  }

  /**
   * 更新预览区域的背景颜色
   */
  updatePreviewColor(colorCircle) {
    if (!this.previewBg || !colorCircle) return;
    
    // 直接获取颜色圆圈的背景样式并应用到预览区域
    const backgroundStyle = window.getComputedStyle(colorCircle).background;
    this.previewBg.style.background = backgroundStyle;
  }

  /**
   * 处理输入框变化 - 字符限制
   */
  handleInputChange(event) {
    let value = event.target.value;
    
    // 限制最多10个字符(包括空格和标点)
    if (value.length > 10) {
      value = value.substring(0, 10);
      event.target.value = value;
    }
  }

  /**
   * 处理提交按钮点击
   */
  handleSubmit() {
    const inputValue = this.input.value.trim();
    
    if (this.isTyping) {
      return; // 如果正在打字,不重复触发
    }
    
    // 清空之前的文本
    this.textDisplay.textContent = '';
    
    if (!inputValue) {
      // 如果输入为空,只清空文本,但背景保持显示
      return;
    }
    
    // 设置字号
    this.setFontSize(inputValue.length);
    
    // 开始打字机效果
    this.typewriterEffect(inputValue);
  }

  /**
   * 根据字符数设置字号
   * @param {number} length - 字符长度
   * PC端：大于7个字符时 72px，小于等于7个字符时 80px
   */
  setFontSize(length) {
    this.textDisplay.classList.remove('size-large', 'size-small');
    
    if (length > 7) {
      this.textDisplay.classList.add('size-small'); // 72px (PC端)
    } else {
      this.textDisplay.classList.add('size-large'); // 80px (PC端)
    }
  }

  /**
   * 打字机效果
   * @param {string} text - 要显示的文本
   */
  typewriterEffect(text) {
    this.isTyping = true;
    this.currentText = text;
    let index = 0;
    
    // 容器始终保持显示
    this.textDisplay.classList.add('is-visible');
    
    const typeInterval = setInterval(() => {
      if (index < text.length) {
        this.textDisplay.textContent += text.charAt(index);
        index++;
      } else {
        clearInterval(typeInterval);
        this.isTyping = false;
      }
    }, 100); // 每100ms显示一个字符
  }

  /**
   * 隐藏文本显示
   */
  hideTextDisplay() {
    this.textDisplay.classList.remove('is-visible');
    setTimeout(() => {
      this.textDisplay.textContent = '';
    }, 300); // 等待淡出动画完成
  }
}

if (!customElements.get('personalized-nameplate')) {
  customElements.define('personalized-nameplate', PersonalizedNameplate);
}
